import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import NotificationSystem from 'rc-notification'
import { useIntercom } from 'react-use-intercom'
import {
  AlertText,
  AlertTitle,
  DangerButton,
  DangerLink,
  WarningButton,
  WarningLink,
} from 'Containers/CampaignsList/components/EmailNotConfirmedAlert/styles'
import { FullWideNotification } from 'Shared/components/Notification'
import { settings } from 'Router/routes'
import Alert from 'Shared/components/Alert'

let notification: {
  notice: (arg0: {
    content: JSX.Element
    position: string
    duration: number
    closable: boolean
    style: { left: number; top: number }
    className: string
  }) => void
} | null = null

const showNotification = (message: string) => {
  notification &&
    notification.notice({
      content: <FullWideNotification color="success" message={message} />,
      position: 'full',
      duration: 3,
      closable: true,
      style: { left: 0, top: 60 },
      className: `ltr-support`,
    })
}

type EmailNotConfirmedAlertProps = {
  sendEmailToConfirmAccount: () => void
  email: string
  isEmailBounced: boolean
  isArtist?: boolean
  isEmailConfirmd?: boolean
}

const EmailNotConfirmedAlert: FunctionComponent<EmailNotConfirmedAlertProps> = ({
  sendEmailToConfirmAccount,
  email,
  isEmailBounced,
  isEmailConfirmd,
  isArtist,
}) => {
  const { t } = useTranslation()
  const { show } = useIntercom()

  const handleSendEmail = () => {
    sendEmailToConfirmAccount()
    NotificationSystem.newInstance(
      { style: { top: 65 } },
      (
        // eslint-disable-next-line no-return-assign
        n: {
          notice: (arg0: {
            content: JSX.Element
            position: string
            duration: number
            closable: boolean
            style: { left: number; top: number }
            className: string
          }) => void
        } | null
      ) => (notification = n)
    )
    showNotification(t('notification.checkEmail'))
  }

  const setErrorMessage = () => {
    if (isEmailBounced && !isEmailConfirmd) {
      return (
        <Alert color="danger" icon>
          <div>
            {isArtist ? (
              <>
                <AlertText>
                  {t('campaignsPage.bouncedEmail')}
                  <br />
                  {t('campaignsPage.bouncedEmailSecond')}&nbsp;
                </AlertText>
                <DangerLink to={`${settings}/email`}>
                  {t('campaignsPage.clickHere')}
                </DangerLink>{' '}
              </>
            ) : (
              <>
                <AlertText>
                  {t('campaignsPage.NotAbleToSendConfirmationEmailTo')} {email}{' '}
                  <br />
                  {t('campaignsPage.contactSupportToReportThisIssue')}&nbsp;
                </AlertText>
                <DangerButton onClick={show}>
                  {t('campaignsPage.contactSupport')}
                </DangerButton>
              </>
            )}
          </div>
        </Alert>
      )
    }
    if (isEmailBounced && isEmailConfirmd) {
      return (
        <Alert color="danger" icon>
          <div>
            {isArtist ? (
              <>
                <AlertText>
                  {t('campaignsPage.bouncedEmail')}
                  <br />
                  {t('campaignsPage.bouncedEmailSecond')}&nbsp;
                </AlertText>
                <DangerLink to={`${settings}/email`}>
                  {t('campaignsPage.clickHere')}
                </DangerLink>{' '}
              </>
            ) : (
              <>
                <AlertText>
                  {t('campaignsPage.theEmailAddress')} {email}{' '}
                  {t('campaignsPage.isInvalidOrNotConfirmed')}. <br />
                  {t('campaignsPage.contactSupportToReportThisIssue')}&nbsp;
                </AlertText>
                <DangerButton onClick={show}>
                  {t('campaignsPage.contactSupport')}
                </DangerButton>
              </>
            )}
          </div>
        </Alert>
      )
    }

    if (email) {
      return (
        <Alert color="warning" icon>
          {isArtist ? (
            <>
              <AlertText>
                <AlertTitle>
                  {t('createCampaignPage.emailConfirmation')}
                </AlertTitle>
                <div>
                  {t('createCampaignPage.checkEmail')} {email}{' '}
                  {t('createCampaignPage.andСlickСonfirmationLink')}
                </div>
                <div>
                  {t('campaignsPage.emailAccess')}
                  <WarningLink to={`${settings}/email`}>
                    {t('campaignsPage.here')}
                  </WarningLink>
                </div>
              </AlertText>
              <WarningButton type="button" onClick={handleSendEmail}>
                {t('campaignsPage.sendAgain')}
              </WarningButton>
            </>
          ) : (
            <>
              <AlertText>
                {t('createCampaignPage.pleaseCheckYourEmail')} {email}{' '}
                {t('createCampaignPage.andСlickСonfirmationLink')}
                <br />
                <p>
                  {t('createCampaignPage.ifTouDontHaveAccessToThisEmail')}
                  <DangerButton onClick={show}>
                    {t('createCampaignPage.pleaseContactSupport')}
                  </DangerButton>{' '}
                  {t('createCampaignPage.toReportThisIssue')}&nbsp;
                </p>
              </AlertText>
              <WarningButton type="button" onClick={handleSendEmail}>
                {t('campaignsPage.sendAgain')}
              </WarningButton>
            </>
          )}
        </Alert>
      )
    }

    return (
      <Alert color="warning" icon>
        <p>
          {t('campaignsPage.addEmail')}
          <WarningLink to={`${settings}/email`}>
            {t('campaignsPage.here')}
          </WarningLink>
        </p>
      </Alert>
    )
  }

  return <>{setErrorMessage()}</>
}

export default EmailNotConfirmedAlert
